import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import {
  CssBaseline,
  Box,
  useMediaQuery,
  useTheme,
  Container,
  Typography,
  Link,
} from "@mui/material";

import MyAppBar from './EnhancedAppBar';
import { ThemeProvider } from "@mui/material/styles";
import { ColorModeContext, createAppTheme, commonStyles } from 'shared/styles/enhancedTheme';
import { drawerWidth, collapsedDrawerWidth } from './Sidebar';
import Sidebar from './Sidebar';
import TermsOfServiceDialog from '../../settings/TermsOfServiceDialog';
import PrivacyPolicyDialog from '../../settings/PrivacyPolicyDialog';
import { useUserContext } from 'shared/contexts/UserContext';
import { axiosService } from 'shared/helpers/axios';
import EmailVerificationBanner from './EmailVerificationBanner';
import DuplicateAccountsGate from './DuplicateAccountsGate';
import PendingInvitationsReminder from './PendingInvitationsReminder';
import { preloadLatestCalls } from 'components/collab/funding/LatestCallsCarousel';

const GlobalLayout = ({ children, hideNavigation = false, hasSideBar = true, bgcolor}) => {
  const navigate = useNavigate();
  const { user, refreshUser, isAuthenticated } = useUserContext();
  // once the app has settled: the funding page's "New calls" card in the background, so it shows at once there
  React.useEffect(() => {
    if (isAuthenticated) preloadLatestCalls();
  }, [isAuthenticated]);

  // ── Color mode ──────────────────────────────────────────────────────────────
  const [mode, setMode] = useState(() => localStorage.getItem('colorMode') || 'light');
  const toggleColorMode = useCallback(() => {
    setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('colorMode', next);
      return next;
    });
  }, []);
  const appTheme = useMemo(() => createAppTheme(mode), [mode]);

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = sessionStorage.getItem('sidebarOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Dialog state management
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Auto-collapse on mobile
  React.useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  const toggleSidebar = () => {
    const next = !sidebarOpen;
    setSidebarOpen(next);
    sessionStorage.setItem('sidebarOpen', JSON.stringify(next));
  };
  
  // Dialog handlers
  const handleTermsAccept = async (newState) => {
    if (!isAuthenticated) return;
    
    try {
      await axiosService.patch('/users/privacy-settings/', { 
        accepted_terms_of_service: newState 
      });
      await refreshUser(); // Refresh user data from backend
      if (newState) {
        setTermsOpen(false);
      }
    } catch (err) {
      console.error('Error updating terms acceptance:', err);
    }
  };
  
  const handlePrivacyAccept = async (consentSettings) => {
    if (!isAuthenticated) return;
    
    try {
      await axiosService.patch('/users/privacy-settings/', { 
        accepted_privacy_policy: true 
      });
      await refreshUser(); // Refresh user data from backend
      setPrivacyOpen(false);
    } catch (err) {
      console.error('Error updating privacy acceptance:', err);
    }
  };

  return (
    <ColorModeContext.Provider value={{ mode, toggleColorMode }}>
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ display: "flex", flex: 1 }}>
        <MyAppBar onToggle={ toggleSidebar } />
        
        {/* Sidebar — always visible, locked collapsed on mobile */}
        {hasSideBar && <Sidebar open={ sidebarOpen} onToggle={toggleSidebar} />}

        {/* Main Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            width: `calc(100% - ${(!isMobile && sidebarOpen) ? drawerWidth : collapsedDrawerWidth}px)`,
            
            transition: theme.transitions.create(['margin-left', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            ...commonStyles.contentArea,
            bgcolor: bgcolor || 'layout.contentArea',
          }}
        >
          {isAuthenticated && <EmailVerificationBanner user={user} />}
          {isAuthenticated && <DuplicateAccountsGate onSettled={refreshUser} />}
          {isAuthenticated && <PendingInvitationsReminder user={user} />}
          {children}
        </Box>

      </Box>




      
      <Box
          component="footer"
          sx={{
            width: !hasSideBar ? '100%' : `calc(100% - ${(!isMobile && sidebarOpen) ? drawerWidth : collapsedDrawerWidth}px)`,
            ml: !hasSideBar ? 0 : `${(!isMobile && sidebarOpen) ? drawerWidth : collapsedDrawerWidth}px`,
            py: 3,
            px: !hasSideBar ? 3 : 2,
            backgroundColor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
            transition: theme.transitions.create(['margin-left', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
>
  <Container maxWidth="xl">
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', sm: 'row' },
      justifyContent: 'space-between', 
      alignItems: 'center',
      gap: 2 
    }}>
      <Typography variant="body2" color="text.secondary">
        © {new Date().getFullYear()} Helixome Life Sciences and Information Technologies Ltd. All rights reserved.
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Link 
          component="button" 
          variant="body2" 
          color="text.secondary"
          onClick={() => setTermsOpen(true)}
          sx={{ textDecoration: 'none', cursor: 'pointer', border: 'none', background: 'none' }}
        >
          Terms
        </Link>
        <Link 
          component="button" 
          variant="body2" 
          color="text.secondary"
          onClick={() => setPrivacyOpen(true)}
          sx={{ textDecoration: 'none', cursor: 'pointer', border: 'none', background: 'none' }}
        >
          Privacy Policy
        </Link>
        <Link 
          component="button" 
          variant="body2" 
          color="text.secondary"
          onClick={() => navigate('/settings/?section=contact')}
          sx={{ textDecoration: 'none', cursor: 'pointer', border: 'none', background: 'none' }}
        >
          Contact
        </Link>
      </Box>
      
      {/* <Box sx={{ display: 'flex', gap: 1 }}>
        <IconButton size="small" href="https://facebook.com/helixome" target="_blank">
          <FacebookIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" href="https://twitter.com/helixome" target="_blank">
          <TwitterIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" href="https://linkedin.com/company/helixome" target="_blank">
          <LinkedInIcon fontSize="small" />
        </IconButton>
      </Box> */}
    </Box>
  </Container>
      </Box>
      </Box>

      {/* Terms and Privacy Dialogs */}
      <TermsOfServiceDialog
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        onAccept={handleTermsAccept}
        isAccepted={user?.accepted_terms_of_service || false}
      />
      
      <PrivacyPolicyDialog
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        onAccept={handlePrivacyAccept}
        showConsentOptions={false}
      />

    </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default GlobalLayout;
